import { useState } from 'react';
import { AxiosError } from 'axios';

/**
 * Tiny hook for showing the most-recent mutation error inline in a form.
 *
 * Pulls the human-readable message out of a Laravel validation response
 * (the first .errors.* line, or .message) when it's an axios error,
 * otherwise falls back to a generic copy.
 *
 * Usage:
 *   const { error, wrap, clear } = useFormError();
 *   await wrap(() => mutation.mutateAsync(...));
 *   // render: {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
 */
export function useFormError(): {
  error: string | null;
  /** Runs `fn`; returns true on success, false (and shows message) on error. */
  wrap: (fn: () => Promise<unknown>) => Promise<boolean>;
  clear: () => void;
} {
  const [error, setError] = useState<string | null>(null);

  async function wrap(fn: () => Promise<unknown>): Promise<boolean> {
    setError(null);
    try {
      await fn();
      return true;
    } catch (err) {
      setError(extractMessage(err));
      return false;
    }
  }

  return { error, wrap, clear: () => setError(null) };
}

export function extractMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { message?: string; errors?: Record<string, string[]> }
      | undefined;

    if (data?.errors) {
      const firstField = Object.values(data.errors)[0];
      if (firstField && firstField.length > 0) {
        return firstField[0];
      }
    }
    if (data?.message) {
      return data.message;
    }
    if (err.response?.status === 419) {
      return 'Your session expired — refresh and try again.';
    }
    if (err.response?.status == null) {
      return 'Could not reach the server.';
    }
    return `Request failed (${err.response.status}).`;
  }
  return 'Something went wrong. Try again.';
}
